import type { FUSportsEvent, FUSportsLivescoreScore, FUSportsLivescoreStatus } from "fansunited-data-layer";

// FUSportsLivescoreTiming isn't in the top-level barrel; reuse the event's own timing type.
type EventTiming = NonNullable<FUSportsEvent["timing"]>;

/**
 * Minimal per-event snapshot served by `GET /api/live/events`. Carries only
 * what changes during a match; names and logos come with the page and don't
 * change mid-game.
 */
export interface LiveEventSnapshot {
    id: string;
    status: FUSportsLivescoreStatus;
    score: FUSportsLivescoreScore[];
    startTime: string | null;
    timing?: EventTiming;
}

/** Shape consumed by `useLiveMatchData` and the live match cards. */
export interface LiveMatchData {
    scoreCompetitorOne: string | null;
    scoreCompetitorTwo: string | null;
    /** Current match minute (e.g. "45"), derived from playedSeconds. */
    currentMinute: string | null;
    statusType: string | null;
    statusCode: string | null;
}

/** Upper bound on ids per request; keeps the URL and the upstream call small. */
export const MAX_EVENT_IDS = 50;

const EVENT_ID_PATTERN = /^[A-Za-z0-9:_-]{1,40}$/;

/**
 * Canonical id list: trimmed, de-duplicated, sorted, invalid entries dropped.
 * Sorting is what lets every client asking for the same set produce the same
 * URL and share one cache entry.
 */
export function canonicalizeEventIds(ids: readonly string[]): string[] {
    return [...new Set(ids.map((s) => s.trim()).filter((id) => id && EVENT_ID_PATTERN.test(id)))].sort();
}

/**
 * Split a canonical list into fixed-size chunks for the URL. Deterministic
 * (same input → same chunks) so chunked requests still share edge entries.
 */
export function chunkEventIds(ids: readonly string[], size: number = MAX_EVENT_IDS): string[][] {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
    return chunks;
}

/**
 * Server-side parse of the `ids` query value. Returns null when the input is
 * empty, contains an invalid id, or exceeds MAX_EVENT_IDS; clients are
 * expected to chunk (see liveEventsClient.ts) rather than send more.
 */
export function parseEventIds(raw: string | null): string[] | null {
    if (!raw) return null;
    const parts = raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    const ids = canonicalizeEventIds(parts);
    if (ids.length === 0 || ids.length !== new Set(parts).size || ids.length > MAX_EVENT_IDS) return null;
    return ids;
}

export function toSnapshot(event: FUSportsEvent): LiveEventSnapshot {
    return {
        id: event.id,
        status: event.status,
        score: event.score,
        startTime: event.startTime,
        timing: event.timing,
    };
}

export function toLiveMatchData(snapshot: LiveEventSnapshot): LiveMatchData {
    // Score comes from the `result` key (current total). Fall back to the
    // first entry if the API ever drops the explicit `result` tag.
    const result = snapshot.score?.find((s) => s.key === "result") ?? snapshot.score?.[0];
    const played = snapshot.timing?.playedSeconds;
    const currentMinute = typeof played === "number" && played > 0 ? String(Math.floor(played / 60)) : null;
    return {
        scoreCompetitorOne: result?.competitorOne ?? null,
        scoreCompetitorTwo: result?.competitorTwo ?? null,
        currentMinute,
        statusType: snapshot.status?.type ?? null,
        statusCode: snapshot.status?.code ?? null,
    };
}
